import { useEffect, useState } from 'react';

export const useDeviceFingerprint = (hasConsent: boolean) => {
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasConsent) {
      setFingerprint(null);
      setError(null);
      return;
    }

    const generateFingerprint = async () => {
      setIsGenerating(true);
      setError(null);

      try {
        // Vérifier le support de l'API Crypto
        if (!window.crypto?.subtle) {
          throw new Error('Crypto API non supportée par ce navigateur');
        }

        // Collecter les composants du fingerprint
        const components = [
          navigator.userAgent,
          navigator.language,
          navigator.languages?.join(',') || '',
          new Date().getTimezoneOffset().toString(),
          screen.width.toString(),
          screen.height.toString(),
          screen.colorDepth.toString(),
          screen.pixelDepth?.toString() || '',
          navigator.platform,
          navigator.cookieEnabled.toString(),
          navigator.doNotTrack || 'unknown',
          // Ajouter d'autres propriétés disponibles
          navigator.hardwareConcurrency?.toString() || '',
          navigator.maxTouchPoints?.toString() || '',
          window.devicePixelRatio?.toString() || ''
        ];

        // Créer une chaîne unique à partir des composants
        const fingerprintString = components.filter(Boolean).join('|');
        
        // Hasher avec SHA-256
        const encoder = new TextEncoder();
        const data = encoder.encode(fingerprintString);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        
        // Convertir en hexadécimal
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        
        setFingerprint(hashHex);
        
        console.log('🔍 Device fingerprint généré:', {
          componentsCount: components.filter(Boolean).length,
          fingerprintLength: hashHex.length,
          hasConsent
        });
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
        setError(errorMessage);
        console.error('❌ Erreur génération fingerprint:', errorMessage);
      } finally {
        setIsGenerating(false);
      }
    };

    generateFingerprint();
  }, [hasConsent]);

  return {
    fingerprint,
    isGenerating,
    error,
    hasConsent
  };
};

// Hook utilitaire pour vérifier le support du fingerprinting
export const useDeviceFingerprintSupport = () => {
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [supportDetails, setSupportDetails] = useState<{
    crypto: boolean;
    userAgent: boolean;
    screen: boolean;
  }>({
    crypto: false,
    userAgent: false,
    screen: false
  });

  useEffect(() => {
    const checkSupport = () => {
      const crypto = !!(window.crypto?.subtle);
      const userAgent = !!(navigator.userAgent);
      const screen = !!(window.screen);

      const details = { crypto, userAgent, screen };
      setSupportDetails(details);
      setIsSupported(crypto && userAgent && screen);
    };

    checkSupport();
  }, []);

  return {
    isSupported,
    supportDetails
  };
};